import {combineReducers} from 'redux'
import {createAction, handleActions} from 'redux-actions'
import {combineEpics, ofType} from 'redux-observable'
import {concat, of, throwError} from 'rxjs'
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  takeWhile,
} from 'rxjs/operators'
import {FirebaseApi} from '../../api'
import {showPaywall} from '../app'
import {
  getAuthenticatedUserId,
  getIsAuthenticatedUserAllowedPremiumContent,
  userUnauthenticated,
} from '../auth'

// The export runs as a background job in remente-app-backend
// (firebase-functions-v2/src/user-data/export.ts):
// 1. The `user-exportUserData` callable creates (or reuses) a job and returns its jobId.
// 2. Progress is written to Firestore at user_data_exports/{uid}.
// 3. When completed, the zip is available in Storage at `storagePath`.

export const exportData = createAction('remente/dataExport/exportData')
export const exportDataStarted = createAction(
  'remente/dataExport/exportDataStarted',
)
export const exportDataProgressed = createAction(
  'remente/dataExport/exportDataProgressed',
)
export const exportDataFulfilled = createAction(
  'remente/dataExport/exportDataFulfilled',
)
export const exportDataRejected = createAction(
  'remente/dataExport/exportDataRejected',
)

const TERMINAL_JOB_STATUSES = ['completed', 'failed']

/**
 * Reducers
 */

const initialState = {
  status: 'idle', // idle | requesting | queued | running | completed | failed
  completedDatasets: 0,
  totalDatasets: 0,
  downloadUrl: null,
}

const exportReducer = handleActions(
  {
    [exportDataStarted]: () => ({...initialState, status: 'requesting'}),
    [exportDataProgressed]: (state, {payload}) => ({
      ...state,
      status: payload.status,
      completedDatasets: payload.completedDatasets || 0,
      totalDatasets: payload.totalDatasets || 0,
    }),
    [exportDataFulfilled]: (state, {payload}) => ({
      ...state,
      status: 'completed',
      completedDatasets: state.totalDatasets,
      downloadUrl: payload.downloadUrl,
    }),
    [exportDataRejected]: () => ({...initialState, status: 'failed'}),
    // Never leak a previous user's (tokenized) download URL to the next user.
    [userUnauthenticated]: () => initialState,
  },
  initialState,
)

export default combineReducers({
  export: exportReducer,
})

/**
 * Selectors
 */

export const getDataExport = ({dataExport}) => dataExport.export

/**
 * Epics
 */

const observeExportJob = (uid, jobId) =>
  FirebaseApi.observeUserDataExport(uid).pipe(
    filter(job => !!job && job.jobId === jobId),
    takeWhile(job => !TERMINAL_JOB_STATUSES.includes(job.status), true),
    mergeMap(job => {
      if (job.status === 'completed')
        return FirebaseApi.getStorageDownloadUrl(job.storagePath).pipe(
          map(downloadUrl => exportDataFulfilled({downloadUrl})),
        )
      if (job.status === 'failed')
        return throwError(new Error(job.error || 'Export failed'))
      return of(
        exportDataProgressed({
          status: job.status,
          ...(job.progress || {}),
        }),
      )
    }),
  )

const dataExportEpic = (action$, state$) =>
  action$.pipe(
    ofType(exportData().type),
    // Ignore further clicks while an export is in progress.
    exhaustMap(() => {
      if (!getIsAuthenticatedUserAllowedPremiumContent(state$.value))
        return of(showPaywall())

      const uid = getAuthenticatedUserId(state$.value)
      return concat(
        of(exportDataStarted()),
        FirebaseApi.requestUserDataExport().pipe(
          switchMap(({jobId}) => observeExportJob(uid, jobId)),
          catchError(err => {
            console.error('User data export failed', err)
            return of(exportDataRejected())
          }),
        ),
      ).pipe(takeUntil(action$.pipe(ofType(userUnauthenticated().type))))
    }),
  )

export const dataExportEpics = combineEpics(dataExportEpic)
